import React from 'react';
import PropTypes from 'prop-types';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import { integration, utils, template } from '@yola/ws-sdk';
import { useSelector } from 'react-redux';
import ModalWithPanels from 'src/js/modules/common/components/modal-with-panels';
import fieldNames from '../constants/field-names';

const { ControlGroup, FieldStatus, Stack, SimpleSelectField, CheckboxField, InputGroupField, Box } =
  designSystem;

const { useForm } = reactHookForm;

const FLOATING_ELEMENT_SELECTOR = '.ws-ui-base-select-option, .ws-ui-base-select__option';

const defaultFieldsValidation = {
  pageTemplate: utils.noop,
  pageTitle: utils.noop,
};

function NewPageDialog({
  width = 360,
  height = 244,
  offsetX = 0,
  offsetY = 0,
  captions: {
    title,
    description,
    textInputLabel,
    selectLabel,
    checkboxLabel,
    submit,
    cancel,
    searchPlaceholder,
  },
  onCancel = utils.noop,
  onSubmit = utils.noop,
  fieldsValidation = defaultFieldsValidation,
  pageTemplate = null,
  pageTitle = '',
  isCheckboxAvailable = true,
  isCheckboxChecked = true,
  options,
}) {
  const limits = useSelector(integration.selectors.getLimits);
  const isShopPageTemplateAvailable = limits.shopPageTemplateLimit.available;

  const pageTemplateOptions = isShopPageTemplateAvailable
    ? options
    : options.filter((option) => option.value !== template.constants.SHOP_PAGE_TEMPLATE);
  const initialPageTemplate =
    (pageTemplate && pageTemplateOptions.find(({ value }) => value === pageTemplate)) ||
    pageTemplateOptions[0];

  const initialValues = {
    [fieldNames.PAGE_NAME_INPUT]: pageTitle,
    [fieldNames.PAGE_TEMPLATE_SELECT]: initialPageTemplate.value,
    ...(isCheckboxAvailable && { [fieldNames.DISPLAY_IN_NAVIGATION_CHECKBOX]: isCheckboxChecked }),
  };

  const { handleSubmit, control } = useForm({
    defaultValues: initialValues,
  });

  return (
    <ModalWithPanels
      onCancel={onCancel}
      onSubmit={handleSubmit(onSubmit)}
      onOverlayClick={onCancel}
      width={width}
      height={height}
      offsetX={offsetX}
      offsetY={offsetY}
      captions={{
        title,
        submitButton: submit,
        cancelButton: cancel,
      }}
      floatingModalElementSelector={FLOATING_ELEMENT_SELECTOR}
      centered
      draggable={false}
      overlay="visible"
    >
      <Stack gap="spacing-m">
        {pageTemplate && (
          <ControlGroup title={selectLabel}>
            <SimpleSelectField
              name={fieldNames.PAGE_TEMPLATE_SELECT}
              control={control}
              defaultValue={initialPageTemplate}
              options={pageTemplateOptions}
              placeholder={searchPlaceholder}
              rules={{
                validate: fieldsValidation.pageTemplate,
              }}
              preventOverflow
            />
          </ControlGroup>
        )}
        <Box>
          <ControlGroup title={textInputLabel}>
            <InputGroupField
              control={control}
              name={fieldNames.PAGE_NAME_INPUT}
              rules={{
                validate: fieldsValidation.pageTitle,
              }}
            />
          </ControlGroup>
          <FieldStatus text={description} />
          <Box marginTop="spacing-xs">
            {isCheckboxAvailable && (
              <CheckboxField
                id={fieldNames.DISPLAY_IN_NAVIGATION_CHECKBOX}
                name={fieldNames.DISPLAY_IN_NAVIGATION_CHECKBOX}
                control={control}
                checked={isCheckboxChecked}
                label={checkboxLabel}
              />
            )}
          </Box>
        </Box>
      </Stack>
    </ModalWithPanels>
  );
}

NewPageDialog.propTypes = {
  width: PropTypes.number,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  captions: PropTypes.shape({
    title: PropTypes.string.isRequired,
    textInputLabel: PropTypes.string.isRequired,
    selectLabel: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    checkboxLabel: PropTypes.string.isRequired,
    submit: PropTypes.string.isRequired,
    cancel: PropTypes.string.isRequired,
    searchPlaceholder: PropTypes.string.isRequired,
    errors: PropTypes.shape({
      required: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  fieldsValidation: PropTypes.shape({
    pageTemplate: PropTypes.func,
    pageTitle: PropTypes.func,
  }),
  pageTitle: PropTypes.string,
  pageTemplate: PropTypes.string,
  isCheckboxAvailable: PropTypes.bool,
  isCheckboxChecked: PropTypes.bool,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default NewPageDialog;
